import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

export interface SelectableMember {
  id: string;
  nombre: string;
  color: string;
}

interface Props {
  value: string; // id seleccionado o '' si ninguno
  onChange: (id: string) => void;
  members: SelectableMember[];
  placeholder?: string;
}

// Desplegable de una sola persona con el mismo lenguaje visual que el picker de
// seguidores (avatar monograma, popover blanco redondeado). Sustituye al <select>
// nativo del sistema para dar coherencia a la vista de creación de hilo.
export default function PersonSelect({ value, onChange, members, placeholder }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const selected = members.find((m) => m.id === value) ?? null;

  return (
    <div ref={wrapRef} style={styles.wrap}>
      <button
        type="button"
        className="st-field"
        onClick={() => setOpen((o) => !o)}
        style={{ ...styles.control, ...(open ? styles.controlOpen : null) }}
      >
        {selected ? (
          <span style={styles.value}>
            <span style={{ ...styles.mono, background: selected.color }}>
              {(selected.nombre.charAt(0) || '?').toUpperCase()}
            </span>
            {selected.nombre}
          </span>
        ) : (
          <span style={styles.placeholder}>{placeholder ?? '— Elige una persona —'}</span>
        )}
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
          style={{ color: '#94a3b8', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .12s ease' }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div style={styles.popover}>
          {members.map((m) => {
            const active = m.id === value;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => { onChange(m.id); setOpen(false); }}
                style={{ ...styles.item, ...(active ? styles.itemActive : null) }}
              >
                <span style={{ ...styles.mono, background: m.color }}>
                  {(m.nombre.charAt(0) || '?').toUpperCase()}
                </span>
                <span style={{ flex: 1 }}>{m.nombre}</span>
                {active && (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  wrap: { position: 'relative', width: '100%' },
  control: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 42,
    padding: '8px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    background: '#fff',
    cursor: 'pointer',
    fontSize: 14,
    color: '#0f172a',
    textAlign: 'left',
  },
  controlOpen: { borderColor: '#94a3b8' },
  value: { display: 'inline-flex', alignItems: 'center', gap: 8, flex: 1, fontWeight: 500 },
  placeholder: { flex: 1, color: '#94a3b8' },
  mono: {
    width: 22, height: 22, minWidth: 22, borderRadius: 11, color: '#fff', fontSize: 11, fontWeight: 700,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  },
  popover: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    boxShadow: '0 8px 20px rgba(15,23,42,0.1)',
    zIndex: 50,
    maxHeight: 260,
    overflowY: 'auto',
    padding: 4,
  },
  item: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 14,
    color: '#0f172a',
    textAlign: 'left',
    borderRadius: 8,
  },
  itemActive: { background: '#f1f5f9' },
};
